import React, {useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTitle from "../../components/generics/page-title";
import { Card, CardHeader, CardContent } from '../../components/ui/card';
import {
  Clock,
  Calendar,
  Wallet,
  XCircle,
  Grid,
  List
} from 'lucide-react';
import ReservationSection from '../../components/dashboard/reservation-section';
import AppointmentForm from '../../components/dashboard/reservation-dashboard';
import DailyRevenuePanel from '../../components/dashboard/daily-revenue-panel';
import CollaboratorSelect from '../../components/dashboard/collaborator-select';
import {observer} from "mobx-react-lite";
import professionalStore from "../../store/professional-store";
import companyStore from "../../store/company-store";
import { useDashboardViewModel } from "../../view-model/professional/dashboard-view-model";
import { useTranslation } from 'react-i18next';
import { getFilteredAppointments } from '../../components/onboarding/mock/appointments-mock';
import { SECTION_DASHBOARD } from "../../constants/onboarding-sections";
import OnboardingTour from '../onboarding/onboarding-tour';
import ReviewTutorialButton from '../../components/onboarding/review-tutorial-button';
import { useOnboardingViewModel } from '../../view-model/onboarding/onboarding-view-model';


const Dashboard = observer(() => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const viewModel = useDashboardViewModel();
  const onboardingViewModel = useOnboardingViewModel();

  // Charger les rendez-vous au montage du composant
  useEffect(() => {
    viewModel.initialize().then();

    return () => viewModel.dispose();
  }, [viewModel]);

  // Charger la compagnie sélectionnée pour le select des collaborateurs et initialiser le collaborateur connecté
  useEffect(() => {
    const companyId = companyStore.getIdCompanySelectedDefault;
    const currentUserId = professionalStore.getId;

    if (!companyId || !currentUserId) {
      return;
    }

    companyStore.setCompanyById(companyId);

    // Sélectionner le collaborateur connecté par défaut
    if (companyStore.selectedCompany?.collaboratorCompanies) {
      const currentCollaborator = companyStore.selectedCompany.collaboratorCompanies.find(
        c => c.collaboratorId === currentUserId
      );
      if (currentCollaborator) {
        viewModel.setSelectedCollaboratorId(currentCollaborator.collaboratorCompagnieId);
      }
    }
  }, [companyStore.selectedCompany, professionalStore.getId, viewModel]);

  const handleCollaboratorChange = (collaboratorId: string) => {
    // La reaction MobX dans dashboard-view-model s'occupe déjà de recharger les appointments
    viewModel.setSelectedCollaboratorId(collaboratorId);
  };

  const shouldShowMockData = (
    !onboardingViewModel.getSectionOnboardingStatus(SECTION_DASHBOARD) &&
    onboardingViewModel.getCurrentOnboardingStep(SECTION_DASHBOARD) === 0
  );
  
  const filteredAppointments = shouldShowMockData
    ? getFilteredAppointments()
    : viewModel.getFilteredAppointmentsByActiveFilter();

  return (
    <>
      {/* Composant d'onboarding pour la section dashboard */}
      <OnboardingTour section={SECTION_DASHBOARD}/>
      
      <div className="h-[calc(100vh-2rem)] flex flex-col space-y-3">
      <PageTitle
        title={t('dashboard.title', 'Tableau de bord')}
        subtitle={
          <div className="flex items-center gap-2 text-xs">
            <p>{t('dashboard.greeting', 'Bonjour')} <span className="text-sm font-medium truncate">{professionalStore.getProfileProfessional?.firstName} {professionalStore.getProfileProfessional?.lastName}</span>, {t('dashboard.activity', 'voici l\'activité du')} {viewModel.formatDate(viewModel.currentTime)}</p>
            <p className="text-primary dark:text-primary-light font-medium">
              {viewModel.formatTime(viewModel.currentTime)}
            </p>
          </div>
        }
      />

      {/* Collaborator Select */}
      <div className="flex-none w-full md:w-80">
        <CollaboratorSelect
          value={viewModel.selectedCollaboratorId}
          onChange={handleCollaboratorChange}
        />
      </div>

      <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
        {/* Colonne principale */}
        <div className="col-span-12 md:col-span-7 min-h-0 flex flex-col dashboard-reservations-left">
          {/* Rendez-vous */}
          <Card className="h-full flex flex-col">
            <CardHeader className="border-b pb-3">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-semibold dark:text-white">{t('dashboard.appointmentsOfDay', 'Rendez-vous du jour')}</h2>
                  <span className="bg-primary/10 dark:bg-primary-dark/20 text-primary dark:text-primary-light text-xs font-medium px-2 py-0.5 rounded-full">
                    {shouldShowMockData 
                      ? (filteredAppointments.ongoing?.length || 0) + (filteredAppointments.upcoming?.length || 0) + (filteredAppointments.past?.length || 0)
                      : viewModel.getAllAppointments.length
                    } {t('dashboard.appointments')}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex gap-2">
                    <button
                      onClick={() => viewModel.setActiveFilter('all')}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          viewModel.activeFilter === 'all'
                          ? 'bg-primary/10 dark:bg-primary-dark/20 text-primary dark:text-primary-light'
                          : 'bg-gray-100 dark:bg-gray-750 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t('dashboard.all')}
                    </button>
                    <button
                      onClick={() => viewModel.setActiveFilter('ongoing')}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          viewModel.activeFilter === 'ongoing'
                          ? 'bg-primary/10 dark:bg-primary-dark/20 text-primary dark:text-primary-light'
                          : 'bg-gray-100 dark:bg-gray-750 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t('dashboard.ongoing')} ({filteredAppointments.ongoing ? filteredAppointments.ongoing.length : 0})
                    </button>
                    <button
                      onClick={() => viewModel.setActiveFilter('upcoming')}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          viewModel.activeFilter === 'upcoming'
                          ? 'bg-primary/10 dark:bg-primary-dark/20 text-primary dark:text-primary-light'
                          : 'bg-gray-100 dark:bg-gray-750 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t('dashboard.upcoming')} ({filteredAppointments.upcoming ? filteredAppointments.upcoming.length : 0})
                    </button>
                    <button
                      onClick={() => viewModel.setActiveFilter('past')}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          viewModel.activeFilter === 'past'
                          ? 'bg-primary/10 dark:bg-primary-dark/20 text-primary dark:text-primary-light'
                          : 'bg-gray-100 dark:bg-gray-750 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t('dashboard.past')} ({filteredAppointments.past ? filteredAppointments.past.length : 0})
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => viewModel.setViewMode('card')}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          viewModel.viewMode === 'card'
                          ? 'bg-primary/10 dark:bg-primary-dark/20 text-primary dark:text-primary-light'
                          : 'bg-gray-100 dark:bg-gray-750 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                      aria-label="Afficher en mode cartes"
                    >
                      <Grid size={18} />
                    </button>
                    <button
                      onClick={() => viewModel.setViewMode('list')}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          viewModel.viewMode === 'list'
                              ? 'bg-primary/10 dark:bg-primary-dark/20 text-primary dark:text-primary-light'
                              : 'bg-gray-100 dark:bg-gray-750 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                      aria-label="Afficher en mode liste"
                    >
                      <List size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto py-2">
              <div className="space-y-4">
                {(viewModel.activeFilter === 'all' || viewModel.activeFilter === 'ongoing') && filteredAppointments.ongoing && filteredAppointments.ongoing.length > 0 && (
                <ReservationSection
                    key={`ongoing-${viewModel.activeFilter}`}
                    title={t('dashboard.ongoing')}
                    appointments={filteredAppointments.ongoing}
                    viewMode={viewModel.viewMode}
                    onStatusChange={shouldShowMockData ? undefined : viewModel.handleStatusChange.bind(viewModel)}
                />
                )}
                
                {(viewModel.activeFilter === 'all' || viewModel.activeFilter === 'upcoming') && filteredAppointments.upcoming && filteredAppointments.upcoming.length > 0 && (
                <ReservationSection
                    key={`upcoming-${viewModel.activeFilter}`}
                    title={t('dashboard.upcoming')}
                    appointments={filteredAppointments.upcoming}
                    count={filteredAppointments.upcoming.length}
                    viewMode={viewModel.viewMode}
                    onStatusChange={shouldShowMockData ? undefined : viewModel.handleStatusChange.bind(viewModel)}
                />
                )}
                
                {(viewModel.activeFilter === 'all' || viewModel.activeFilter === 'past') && filteredAppointments.past && filteredAppointments.past.length > 0 && (
                <ReservationSection
                    key={`past-${viewModel.activeFilter}`}
                    title={t('dashboard.past')}
                    appointments={filteredAppointments.past}
                    count={filteredAppointments.past.length}
                    viewMode={viewModel.viewMode}
                    onStatusChange={viewModel.handleStatusChange.bind(viewModel)}

                />
                )}
                {((viewModel.activeFilter === 'ongoing' && (!filteredAppointments.ongoing || filteredAppointments.ongoing.length === 0)) ||
                  (viewModel.activeFilter === 'upcoming' && (!filteredAppointments.upcoming || filteredAppointments.upcoming.length === 0)) ||
                  (viewModel.activeFilter === 'past' && (!filteredAppointments.past || filteredAppointments.past.length === 0))) && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    {t('dashboard.noAppointments', 'Aucun rendez-vous')} {
                    viewModel.activeFilter === 'ongoing' ? t( 'dashboard.ongoing', 'en cours') :
                        viewModel.activeFilter === 'upcoming' ? t('dashboard.upcoming', 'à venir') :
                      t('dashboard.past', 'passé')
                    }
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Colonne de droite */}
        <div className="col-span-12 md:col-span-5 flex flex-col min-h-0 h-full">
          {/* Actions rapides */}
          <Card className="flex-1 flex flex-col">
            <CardHeader className="py-2">
              <div className="flex justify-between items-center">
                <h2 className="text-xs font-semibold dark:text-white">{t('dashboard.quickActions', 'Actions rapides')}</h2>
                {(viewModel.showNewAppointmentForm || viewModel.showRevenuePanel) && (
                  <button
                    onClick={() => {
                      viewModel.setShowNewAppointmentForm(false);
                      viewModel.setShowRevenuePanel(false);
                    }}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    <XCircle size={16} />
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="py-2 flex-1">
              {/* Affichage des boutons d'action rapide si aucun panneau n'est ouvert */}
              {!viewModel.showNewAppointmentForm && !viewModel.showRevenuePanel && (
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => viewModel.setShowNewAppointmentForm(true)}
                    className="flex flex-col items-center p-2 bg-primary/5 dark:bg-primary-dark/20 hover:bg-primary/10 dark:hover:bg-primary-dark/30 text-primary dark:text-primary-light rounded-lg transition-colors dashboard-new-appointment"
                  >
                    <Calendar size={16} className="mb-1" />
                    <span className="text-xs font-medium">{t('dashboard.newAppointment', 'Nouveau RDV')}</span>
                  </button>
                  <button
                    onClick={() => navigate(`/${currentLanguage}/provider/availability`)}
                    className="flex flex-col items-center p-2 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg transition-colors dashboard-availability"
                  >
                    <Clock size={16} className="mb-1" />
                    <span className="text-xs font-medium">{t('dashboard.availability', 'Disponibilités')}</span>
                  </button>
                  <button
                    onClick={() => viewModel.setShowRevenuePanel(true)}
                    className="flex flex-col items-center p-2 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg transition-colors dashboard-revenue"
                  >
                    <Wallet size={16} className="mb-1" />
                    <span className="text-xs font-medium">{t('dashboard.revenue', 'Recettes du jour')}</span>
                  </button>
                </div>
              )}

              {/* Formulaire de création de rendez-vous */}
              {viewModel.showNewAppointmentForm && (
                <AppointmentForm
                    onSuccess={() => viewModel.setShowNewAppointmentForm(false)}
                    onCancel={() => viewModel.setShowNewAppointmentForm(false)}
                    showTitle={true}
                />
              )}

              {/* Panneau des recettes du jour */}
              {viewModel.showRevenuePanel && (
                  <DailyRevenuePanel
                      completedRevenue={viewModel.calculateDailyRevenue()}
                      upcomingRevenue={viewModel.calculateUpcomingRevenue()}
                      totalRevenue={viewModel.calculateTotalDailyRevenue()}
                      completedAppointments={viewModel.getAllAppointments.filter(appointment => {
                        const appointmentTime = viewModel.getAppointmentTime(appointment.time);
                        const appointmentEndTime = viewModel.getAppointmentEndTime(appointment);
                        return appointment.status === 'confirmed' && appointmentEndTime <= viewModel.currentTime;
                      }).length}
                      upcomingAppointments={viewModel.getAllAppointments.filter(appointment => {
                        const appointmentTime = viewModel.getAppointmentTime(appointment.time);
                        return appointment.status === 'confirmed' && appointmentTime > viewModel.currentTime;
                      }).length}
                      onClose={() => viewModel.setShowRevenuePanel(false)}
                  />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Bouton pour revoir le tutoriel */}
      <div className="fixed bottom-6 z-50">
        <ReviewTutorialButton section={SECTION_DASHBOARD} />
      </div>
    </div>
    </>
  );
});

export default Dashboard;